import { useCallback, useEffect, useState } from 'react';
import { tenants as tenantsApi, type Tenant } from '../api';
import { formatDate } from '../lib/format';
import { useToast } from '../lib/toast';

export default function CustomersPage() {
  const { push } = useToast();
  const [data, setData] = useState<Tenant[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Tenant | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await tenantsApi.list({ page, limit: 25, search: search || undefined, status: status || undefined });
      setData(res.data);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (e) {
      setError((e as Error).message);
      push('Load failed: ' + (e as Error).message, 'error');
    }
  }, [page, search, status, push]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold">Customers</h1>
        <div className="text-sm text-ink-muted">{total} total</div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        <input placeholder="Search name or email" value={search}
               onChange={e => { setSearch(e.target.value); setPage(1); }}
               className="flex-1 bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600" />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
                className="bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">{error}</div>
      )}
      {data === null && !error && <div className="text-ink-muted text-sm">Loading customers…</div>}
      {data && data.length === 0 && (
        <div className="bg-ink-card border border-ink-border rounded p-8 text-center text-ink-muted text-sm">
          No customers match.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="bg-ink-card border border-ink-border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-bg border-b border-ink-border text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Business</th>
                <th className="text-left px-4 py-2 font-semibold">Email</th>
                <th className="text-left px-4 py-2 font-semibold">Product</th>
                <th className="text-left px-4 py-2 font-semibold">Tier</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {data.map(t => (
                <tr key={t.id}
                    onClick={() => setSelected(t)}
                    className="border-b border-ink-border last:border-0 hover:bg-ink-hover/30 cursor-pointer">
                  <td className="px-4 py-2 font-medium">{t.business_name}</td>
                  <td className="px-4 py-2 text-ink-muted">{t.email}</td>
                  <td className="px-4 py-2">{t.product_name || '—'}</td>
                  <td className="px-4 py-2">{t.tier_name || '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      t.status === 'active' ? 'bg-green-900/40 text-green-300' :
                      t.status === 'trial'  ? 'bg-blue-900/40 text-blue-300' :
                      'bg-ink-border text-ink-text'
                    }`}>{t.status}</span>
                  </td>
                  <td className="px-4 py-2 text-ink-muted">{formatDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
                    className={`px-3 py-1 text-xs rounded ${
                      p === page ? 'bg-brand-600 text-white' : 'bg-ink-card text-ink-text hover:bg-ink-hover border border-ink-border'
                    }`}>
              {p}
            </button>
          ))}
        </div>
      )}

      {selected && <CustomerDetail tenant={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function CustomerDetail({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-ink-card border border-ink-border rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{tenant.business_name}</h2>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-text text-xl leading-none">×</button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-ink-muted">ID:</span> {tenant.id}</div>
          <div><span className="text-ink-muted">Email:</span> {tenant.email}</div>
          <div><span className="text-ink-muted">Status:</span> {tenant.status}</div>
          <div><span className="text-ink-muted">Product:</span> {tenant.product_name || '—'}</div>
          <div><span className="text-ink-muted">Tier:</span> {tenant.tier_name || '—'}</div>
          <div><span className="text-ink-muted">Created:</span> {formatDate(tenant.created_at)}</div>
        </div>
        <div className="mt-4 text-xs text-ink-muted">
          Per-customer entitlement overrides are managed from Subscriptions → Overrides.
        </div>
      </div>
    </div>
  );
}
